import { inject, Type } from '@angular/core';
import { rxResource } from '@angular/core/rxjs-interop';
import {
  withProps,
  withState,
  patchState,
  withMethods,
  signalStoreFeature,
} from '@ngrx/signals';
import { merge, tap, type Observable } from 'rxjs';
import { DialogService, DynamicDialogRef } from '../../exports/primeng';
import type { DialogRefLike } from '../../types/dialog-ref';
import { buildSettingsFormDialogConfig } from '../../utils/dialog-config.utils';

type AuthDialogDeps = {
  resetLoginState: () => void;
  signOut: () => Observable<unknown>;
};

type AuthDialogState = {
  _loginDialogRef: DynamicDialogRef | null;
};

export function withAuthDialog<T extends AuthDialogDeps>(store: T) {
  return signalStoreFeature(
    withState<AuthDialogState>({
      _loginDialogRef: null,
    }),

    withMethods((innerStore, dialog = inject(DialogService)) => ({
      openLoginDialog: (component: Type<unknown>) => {
        if (innerStore._loginDialogRef()) return;

        store.resetLoginState();
        const ref = dialog.open(
          component,
          buildSettingsFormDialogConfig({ width: '31rem' }),
        );

        patchState(innerStore, {
          _loginDialogRef: ref,
        });
      },

      closeLoginDialog: () => innerStore._loginDialogRef()?.close(),

      signOutFromDialog: () => {
        store.signOut().subscribe({ error: () => undefined });
      },
    })),

    withProps((innerStore) => ({
      _loginDialogCloseResource: rxResource({
        params: () => innerStore._loginDialogRef() ?? undefined,
        stream: ({ params: ref }) =>
          merge(
            (ref as DialogRefLike).onClose,
            (ref as DialogRefLike).onDestroy,
          ).pipe(
            tap(() => {
              store.resetLoginState();
              patchState(innerStore, {
                _loginDialogRef: null,
              });
            }),
          ),
      }),
    })),
  );
}
